import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import debounce from 'lodash.debounce'
import { CharField } from 'components/forms'
import { FormattedEnergy } from 'shared/units'
import { Button, Loader } from 'components/util'
import styles from './_calorieCalculator.css'
import { KILOJOULES, KILOCALORIES } from 'shared/units/constants'
import { fetchCalories } from 'services/workouts/actions'
import { selectUnitPreference } from 'services/user/selectors'
import { getFormValues, change } from 'redux-form'
import { IMPERIAL } from 'shared/units/constants'
import { convert } from 'shared/units'
import FieldError from 'components/forms/FieldError'

const AUTO_CALCULATE_CALORIES = 'autoCalculateCalories'

const mapStateToProps = (state, props) => ({
  unitPreference: selectUnitPreference(state),
  values: getFormValues(props.formName)(state),
})

const mapDispatchToActions = {
  change,
  fetchCalories,
}

export class CalorieCalculator extends Component {

  static propTypes = {
    formName: PropTypes.string,
    unitPreference: PropTypes.string,
    fetchCalories: PropTypes.func,
    values: PropTypes.shape({
      speed: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
      pace: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
      activityType: PropTypes.string,
      distance: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
      duration_hh: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
      duration_mm: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
      duration_ss: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
    }),
    change: PropTypes.func,
  };

  static defaultProps = {
    values: {},
  };

  state = {
    autoCalculateCalories: false,
  }

  componentDidMount () {
    const autoCalculateCalories = localStorage.getItem(AUTO_CALCULATE_CALORIES)
    this.updateAutoCalculateCalories(JSON.parse(autoCalculateCalories))
  }

  componentWillReceiveProps (nextProps) {
    if (this.state.autoCalculateCalories && nextProps && nextProps.values) {
      this.checkForCalorieChange(nextProps.values)
    }
  }

  checkForCalorieChange = (nextValues, force = false) => {
    const { speed, pace, distance, duration_hh: hh, duration_mm: mm, duration_ss: ss } = nextValues
    const props = ['speed', 'pace', 'distance', 'duration_hh', 'duration_mm', 'duration_ss']
    const caloriePropUpdated = props.some(prop => nextValues[prop] !== this.props.values[prop])
    const duration = hh || mm || ss
    const covered = (speed || pace) && distance

    if ((caloriePropUpdated || force) && (duration || covered)) {
      this.handleCalcCalories()
    }
  }

  handleAutoCalculateChange = (evt) => {
    const checked = evt.target.checked
    this.updateAutoCalculateCalories(checked)
    if (checked) {
      this.checkForCalorieChange({}, true)
    }
  }

  updateAutoCalculateCalories (should) {
    localStorage.setItem(AUTO_CALCULATE_CALORIES, !!should)
    this.setState({ autoCalculateCalories: !!should })
  }

  handleCalcCalories = debounce(() => {
    const { unitPreference: system, values } = this.props
    const { speed, pace, activityType, distance, duration_hh, duration_mm, duration_ss } = values

    this.setState({ calculating: true })

    this.props.fetchCalories({
      speed,
      pace,
      activityType,
      distance,
      duration_hh,
      duration_mm,
      duration_ss,
      speedUnit: system === IMPERIAL ? 'mi/h' : 'km/h',
      paceUnit: system === IMPERIAL ? 'min/mi' : 'min/km',
    }).then((res) => {
      const { value: { data: { output: { value, unit }}}} = res
      // depending on user's settings, we might be given 'kJ', this ensures
      // the log workout page always operates in kCal for now, until product decides what
      // to do with the 'display settings'
      const convertedValue = (unit === 'kJ') ? convert(value).from(KILOJOULES).to(KILOCALORIES) : value
      const formattedValue = parseFloat(convertedValue).toFixed(2)

      this.props.change(this.props.formName, 'energy', formattedValue, true)
      this.setState({ calculating: false, calorieError: false })
    }, () => {
      this.setState({ calculating: false })
    })
  }, 750)

  handleRequestCalculate = () => {
    const { speed, pace, distance, duration_hh: hh, duration_mm: mm, duration_ss: ss } = this.props.values
    const hasSpeed = speed || pace
    const hasDistance = !!distance
    const hasDuration = hh || mm || ss

    this.setState({ calorieError: false })

    if ((hasSpeed && hasDistance) || hasDuration) {
      this.setState({ calculating: true })
      this.handleCalcCalories()
    } else {
      this.setState({ calorieError: true })
    }
  }

  render () {
    const { calculating, autoCalculateCalories } = this.state

    return (
      <div>
        <div className={styles.container}>
          <CharField
            bold
            short
            name='energy'
            type='number'
            step='any'
            min='0'
            inputContainerClassName={styles.shortInput}
            labelComponent={(
              <span className={styles.calorieLabel}>
                <FormattedMessage
                  id='workouts.create.calories'
                  defaultMessage='Calories Burned'
                />
                {calculating ?
                  <Loader style={{ marginLeft: 5 }} type='ios' height={14} width={14} />
                : null}
              </span>
            )}
            afterComponent={(
              <div className={styles.afterCalories}>

                <span className={styles.fieldUnit}>
                  <FormattedEnergy
                    id='workouts.create.energy_unit'
                    to={KILOCALORIES}
                  />
                </span>

                {!autoCalculateCalories ?
                  <Button
                    icon='calculator'
                    size='small'
                    disabled={calculating}
                    loading={calculating}
                    className={styles.calculateButton}
                    onClick={this.handleRequestCalculate}
                  >
                    <span className={styles.calculateButtonText}>

                      {calculating
                        ? <FormattedMessage id='workouts.create.calc_calories' defaultMessage='Calculating' />
                        : <FormattedMessage id='workouts.create.calcing_calories' defaultMessage='Calculate' />
                      }

                    </span>
                  </Button>
                : null}

              </div>
            )}
          />
          <label
            className={styles.autoCalculate}
            htmlFor='autoCalculateCalories'
          >
            <input
              id='autoCalculateCalories'
              type='checkbox'
              checked={autoCalculateCalories}
              onChange={this.handleAutoCalculateChange}
            />
            <FormattedMessage
              id='workouts.create.auto_calc_calories'
              defaultMessage='Auto-calculate'
            />
          </label>
        </div>
        <div>

          {this.state.calorieError ?
            <FieldError
              className={styles.calcCaloriesError}
              errorComponent={(
                <FormattedMessage
                  id='workout.create.error_calc_calroies'
                  defaultMessage='We were unable to calculate calories burned for your workout. Please input the distance and pace and/or duration.'
                />
              )}
              showError={this.state.calorieError}
            />
          : null}

        </div>
      </div>
    )
  }

}

export default connect(mapStateToProps, mapDispatchToActions)(CalorieCalculator)



// WEBPACK FOOTER //
// ./src/components/workouts/WorkoutForm/CalorieCalculator.jsx